<template>
    <Teleport to="body">
        <div v-if="containerVisible" class="drawer-container" :class="{ 'with-overlay': overlay }"
            @click.self="handleContainerClick">
            <Transition :name="transitionName" @after-leave="onTransitionComplete">
                <div v-show="isVisible" ref="drawerRef" class="drawer" :class="[positionClass]" :style="drawerStyle">
                    <div class="drawer-header" v-if="showCloseButton">
                        <div class="drawer-label">{{ label }}</div>
                        <button class="text" @click="close">
                            <ZSIcon size="x-large" color="var(--color-blue)" icon="Close" />
                        </button>
                    </div>
                    <div class="drawer-content">
                        <slot></slot>
                    </div>
                </div>
            </Transition>
        </div>
    </Teleport>
</template>

<script setup>
import { computed, ref, watch, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue';
// import ZSButton from "@/components/zs-components/ZSButton.vue";
// import ZSIcon from "@/components/zs-components/global/ZSIcon.vue";

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: undefined
    },
    position: {
        type: String,
        default: 'right',
        validator: (value) => ['top', 'right', 'bottom', 'left', 'full'].includes(value)
    },
    dimension: {
        type: [String, Number],
        default: '300px'
    },
    showCloseButton: {
        type: Boolean,
        default: true
    },
    closeOnClickOutside: {
        type: Boolean,
        default: true
    },
    overlay: {
        type: Boolean,
        default: true
    },
    label: {
        type: String,
        default: ''
    },
    zIndex: {
        type: Number,
        default: 100000000
    }
});

const emit = defineEmits(['update:modelValue', 'closed', 'opened']);

const instance = getCurrentInstance();
// A component is "controlled" when v-model is used.
const isControlled = computed(() => props.modelValue !== undefined && !!instance.vnode.props?.['onUpdate:modelValue']);

// Internal state for uncontrolled mode
const internalValue = ref(false);

// Writable computed property to unify state management.
// It uses the prop if controlled, otherwise it uses the internal state.
const isDrawerOpen = computed({
    get() {
        return isControlled.value ? props.modelValue : internalValue.value;
    },
    set(value) {
        if (isControlled.value) {
            emit('update:modelValue', value);
        } else {
            internalValue.value = value;
        }
    }
});

// Refs for managing the visual state and transitions
const isVisible = ref(isDrawerOpen.value);
const containerVisible = ref(isDrawerOpen.value);
const drawerRef = ref(null);

// Watch the unified state to trigger visibility changes and transitions
watch(isDrawerOpen, (newValue) => {
    if (newValue) {
        containerVisible.value = true;
        // Delay to ensure the container is visible before starting the transition
        setTimeout(() => {
            isVisible.value = true;
            emit('opened');
        }, 10);
    } else {
        isVisible.value = false;
        // The 'closed' event is emitted via onTransitionComplete
    }
});

const onTransitionComplete = () => {
    if (!isVisible.value) {
        containerVisible.value = false;
        emit('closed');
    }
};

// Public methods now simply set the unified state
const close = () => {
    isDrawerOpen.value = false;
};

const open = () => {
    isDrawerOpen.value = true;
};

const handleContainerClick = () => {
    if (props.closeOnClickOutside) {
        close();
    }
};

const handleClickOutside = (event) => {
    if (isDrawerOpen.value &&
        props.closeOnClickOutside &&
        drawerRef.value &&
        !drawerRef.value.contains(event.target)) {
        
        // Prevent closing if clicking inside a dialog (which has higher z-index)
        if (event.target.closest('.zs-dialog-backdrop')) {
            return;
        }

        // Check that the click is not on the container itself to avoid double-closing
        if (!event.target.classList.contains('drawer-container')) {
            close();
        }
    }
};

onMounted(() => {
    document.addEventListener('mousedown', handleClickOutside);
});

onBeforeUnmount(() => {
    document.removeEventListener('mousedown', handleClickOutside);
});

const positionClass = computed(() => `drawer-${props.position}`);

const transitionName = computed(() => {
    switch (props.position) {
        case 'top': return 'slide-down';
        case 'right': return 'slide-left';
        case 'bottom': return 'slide-up';
        case 'left': return 'slide-right';
        case 'full': return 'fade';
        default: return 'slide-left';
    }
});

const drawerStyle = computed(() => {
    const style = { zIndex: props.zIndex };
    const dimensionValue = typeof props.dimension === 'number'
        ? `${props.dimension}px`
        : props.dimension;

    if (props.position === 'full') {
        style.width = '100%';
        style.height = '100%';
    } else if (['left', 'right'].includes(props.position)) {
        style.width = dimensionValue;
        style.height = '100%';
    } else {
        style.height = dimensionValue;
        style.width = '100%';
    }

    return style;
});

// Expose methods for parent components
defineExpose({
    open,
    close
});
</script>

<style scoped>
.drawer-container {
    position: var(--zs-drawer-container-position, fixed);
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: v-bind('zIndex');
    transition: var(--zs-drawer-container-transition, background-color 0.3s ease);
}

.drawer-container.with-overlay {
    background-color: var(--zs-drawer-overlay-bg-color, rgba(0, 0, 0, 0.5));
}

.drawer {
    position: var(--zs-drawer-position, fixed);
    background-color: var(--zs-drawer-bg-color, white);
    box-shadow: var(--zs-drawer-box-shadow, 0 0 10px rgba(0, 0, 0, 0.2));
    overflow-y: var(--zs-drawer-overflow-y, auto);
}

.drawer-header {
    margin-top: var(--zs-drawer-header-margin-top, 0.6em);
    padding: var(--zs-drawer-header-padding, 10px 20px);
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.drawer-label {
    font-size: var(--zs-drawer-label-font-size, 20px);
    font-family: var(--zs-drawer-label-font-family, "Poppins");
    color: var(--zs-drawer-label-color, var(--color-menu));
    flex-grow: 1;
    text-align: center;
    margin-left: 2.5rem; /* Adjust to center the label properly when the button is present */
}

.drawer-header .text {
    background: none;
    border: none;
    cursor: pointer;
    padding: 0;
    margin-left: auto; /* Pushes the button to the right */
    display: flex;
    align-items: center;
    justify-content: center;
}


.drawer-content {
    padding: var(--zs-drawer-content-padding, 0 20px);
}

.drawer-left {
    top: 0;
    left: 0;
    bottom: 0;
}

.drawer-right {
    top: 0;
    right: 0;
    bottom: 0;
}

.drawer-top {
    top: 0;
    left: 0;
    right: 0;
}

.drawer-bottom {
    bottom: 0;
    left: 0;
    right: 0;
}

.drawer-full {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
}

/* Transitions */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active,
.slide-up-enter-active,
.slide-up-leave-active,
.slide-down-enter-active,
.slide-down-leave-active,
.fade-enter-active,
.fade-leave-active {
    transition: transform var(--zs-drawer-transition-duration, 0.3s) var(--zs-drawer-transition-easing, ease),
        opacity var(--zs-drawer-transition-duration, 0.3s) var(--zs-drawer-transition-easing, ease);
}

.slide-left-enter-from,
.slide-left-leave-to {
    transform: translateX(100%);
}

.slide-right-enter-from,
.slide-right-leave-to {
    transform: translateX(-100%);
}

.slide-up-enter-from,
.slide-up-leave-to {
    transform: translateY(100%);
}

.slide-down-enter-from,
.slide-down-leave-to {
    transform: translateY(-100%);
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: scale(0.95);
}
</style>